import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Pencil, Plus } from '@factorialco/f0-react/icons/app'
import {
  JobsAndRatesDataCollectionDocument,
  JobsAndRatesDataCollectionItemFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import DeleteJobRoleModal from 'modules/project_management/components/modals/DeleteJobRoleModal'
import { getPeopleCount } from 'modules/project_management/lib/constants'
import { convertCentsToPercentage } from 'modules/project_management/lib/profitabilityCalculations'

import AddJobsAndRatesSidepanel from './AddJobsAndRatesSidepanel'
import EditJobRatesSidepanel from './EditJobRatesSidepanel'

let totalPeople = 0

interface JobsAndRatesDataCollectionProps {
  canManageRates: boolean
  openAddSidepanel?: boolean
  setOpenAddSidepanel?: (open: boolean) => void
}

const JobsAndRatesDataCollection = ({
  canManageRates,
  openAddSidepanel,
  setOpenAddSidepanel,
}: JobsAndRatesDataCollectionProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [rateIdToDelete, setRateIdToDelete] = useState<number | null>(null)
  const [internalShowAddSidePanel, setInternalShowAddSidePanel] = useState<boolean>(false)
  const [existingResourceJobLevelIds, setExistingResourceJobLevelIds] = useState<string[]>([])

  const [showEditJobRatesSidepanel, setShowEditJobRatesSidepanel] = useState<boolean>(false)

  // Use external state if provided, otherwise use internal state
  const showAddSidePanel = openAddSidepanel ?? internalShowAddSidePanel
  const setShowAddSidePanel = setOpenAddSidepanel ?? setInternalShowAddSidePanel
  const [selectedRoleLevelInfo, setSelectedRoleLevelInfo] = useState<{
    name: string
    id: number
    maxCostRate: number
    billingRateCents: number
    profitabilityPercentageCents: number
    currency: string
  }>({
    name: '',
    id: 0,
    maxCostRate: 0,
    billingRateCents: 0,
    profitabilityPercentageCents: 0,
    currency: '',
  })

  const source = useGraphqlDataCollectionSource({
    document: JobsAndRatesDataCollectionDocument,
    totalItemSummary: (totalItems) =>
      `${totalItems} ${i18n.t('common.jobs')}, ${getPeopleCount(totalPeople)}`,
    mapData: (data) => {
      const nodes = data.projectManagement.ratesConnection.nodes

      const ids = nodes
        .map((node) => node.resourceJobTreeLevel?.id)
        .filter(Boolean)
        .map((id) => String(id))

      setExistingResourceJobLevelIds(ids)

      // Calculate total people across all rates
      totalPeople = nodes.reduce((sum, node) => {
        return sum + (node.referencedRatesConnection?.totalCount ?? 0)
      }, 0)
      return nodes
    },
    mapVariables: ({ search, sortings }) => ({
      sortOrder: sortings?.[0],
      roleOrLevel: search,
    }),
    mapPagination: (data) => {
      return {
        total: data.projectManagement.ratesConnection.totalCount ?? 0,
      }
    },
    itemActions: (item) => {
      if (!canManageRates) return
      if (item.id !== null) {
        return [
          {
            label: i18n.t('project_management.jobs_and_rates.table.options.edit_rate'),
            icon: Pencil,
            disabled: !item.resourceJobTreeLevel?.id,
            onClick: () => {
              setSelectedRoleLevelInfo({
                name: item.resourceJobTreeLevel?.name ?? '',
                id: item.resourceJobTreeLevel?.id ?? 0,
                maxCostRate: item.chargingCostCents,
                billingRateCents: item.billingRateCents,
                profitabilityPercentageCents: item.profitabilityPercentageCents,
                currency: item.currency,
              })
              setShowEditJobRatesSidepanel(true)
            },
          },
          {
            label: i18n.t('project_management.jobs_and_rates.table.options.delete_rate'),
            icon: Delete,
            critical: true,
            onClick: () => {
              setRateIdToDelete(item.id)
              setShowDeleteModal(true)
            },
          },
        ]
      }
      return undefined
    },
    itemUrl: (item) => {
      return `${navigation.projects.jobsAndRates.jobLevelId.fullPath({
        jobLevelId: Number(item.resourceJobTreeLevel?.id),
      })}`
    },
    primaryActions: () => {
      if (!canManageRates) return
      return {
        label: i18n.t('project_management.jobs_and_rates.table.main_button'),
        icon: Plus,
        onClick: () => {
          setShowAddSidePanel(true)
        },
      }
    },
    search: {
      enabled: true,
    },
  })

  return (
    <Box paddingTop="s48">
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('project_management.jobs_and_rates.table.empty_state.title'),
            description: i18n.t('project_management.jobs_and_rates.table.empty_state.description'),
            emoji: '💰',
            actions: [
              {
                label: i18n.t('project_management.jobs_and_rates.table.empty_state.button_text'),
                icon: Plus,
                onClick: () => {
                  setShowAddSidePanel(true)
                },
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.role.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.role.tooltip_message'
                  ),
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: item.resourceJobTreeLevel?.ancestor?.name,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.job_level'),
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: item.resourceJobTreeLevel?.name,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.cost_rate.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.cost_rate.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item.chargingCostCents,
                        currency: item.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.bill_rate.label'),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.bill_rate.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: MoneyWithCurrency({
                        amount: item.billingRateCents,
                        currency: item.currency,
                        optionalDecimals: true,
                        locale,
                      }),
                    }
                  },
                },
                {
                  label: i18n.t(
                    'project_management.jobs_and_rates.table.headers.profit_margin.label'
                  ),
                  info: i18n.t(
                    'project_management.jobs_and_rates.table.headers.profit_margin.tooltip_message'
                  ),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'text',
                      value: `${convertCentsToPercentage(item.profitabilityPercentageCents)}%`,
                    }
                  },
                },
                {
                  label: i18n.t('project_management.jobs_and_rates.table.headers.people'),
                  align: 'right',
                  render: (item: JobsAndRatesDataCollectionItemFragment) => {
                    return {
                      type: 'number',
                      value: item.referencedRatesConnection?.totalCount ?? 0,
                    }
                  },
                },
              ]),
            },
          },
        ]}
      />
      {showDeleteModal && rateIdToDelete && (
        <DeleteJobRoleModal
          rateId={rateIdToDelete}
          onClose={() => {
            setShowDeleteModal(false)
            setRateIdToDelete(null)
          }}
        />
      )}
      <AddJobsAndRatesSidepanel
        showAddSidePanel={showAddSidePanel}
        onClose={() => setShowAddSidePanel(false)}
        existingResourceJobLevelIds={existingResourceJobLevelIds}
      />
      <EditJobRatesSidepanel
        showSidePanel={showEditJobRatesSidepanel}
        onClose={() => setShowEditJobRatesSidepanel(false)}
        selectedRoleLevelInfo={selectedRoleLevelInfo}
        refetchQueries={[JobsAndRatesDataCollectionDocument]}
      />
    </Box>
  )
}

export default JobsAndRatesDataCollection
